import { useEffect, useRef } from 'react';
import { getCookie } from 'src/utils/cookies';
import { useProfile } from 'src/hooks/useProfile';
import { SSE_STREAM_URL } from 'src/constants/httpEndPoints';
import {
  openSseStream,
  isTerminal,
  SseEventType,
  SsePayload,
} from 'src/utils/sseClient';

export interface MultiJobStreamOptions {
  type: SseEventType;
  jobIds: string[];
  enabled?: boolean;
  onData?: (jobId: string, data: Record<string, unknown> | Array<Record<string, unknown>> | null) => void;
  onStatus?: (jobId: string, payload: SsePayload) => void;
  onJobDone?: (jobId: string, payload: SsePayload) => void;
  onAllDone?: () => void;
  onError?: (jobId: string, err: Error) => void;
  maxRetries?: number;
  baseDelay?: number;
}

/**
 * Manages N concurrent SSE streams, one per jobId.
 * Opens a new stream when a jobId appears in the array, closes it on terminal status or removal.
 * Calls onAllDone when every open stream has received a terminal status.
 */
export function useMultiJobStream({
  type,
  jobIds,
  enabled = true,
  onData,
  onStatus,
  onJobDone,
  onAllDone,
  onError,
  maxRetries = 3,
  baseDelay = 2000,
}: MultiJobStreamOptions) {
  const { emailId, customerId } = useProfile();
  // Map of jobId -> close function
  const streamsRef = useRef<Map<string, () => void>>(new Map());
  // Track which streams have received terminal status
  const doneSetRef = useRef<Set<string>>(new Set());

  // Always-current refs for callbacks — prevents stale closures in SSE handlers
  const onDataRef = useRef(onData);
  const onStatusRef = useRef(onStatus);
  const onJobDoneRef = useRef(onJobDone);
  const onAllDoneRef = useRef(onAllDone);
  const onErrorRef = useRef(onError);
  onDataRef.current = onData;
  onStatusRef.current = onStatus;
  onJobDoneRef.current = onJobDone;
  onAllDoneRef.current = onAllDone;
  onErrorRef.current = onError;


  useEffect(() => {
    if (!enabled || !type || !emailId || !customerId || jobIds.length === 0) {
      return;
    }

    const token = getCookie('user');
    if (!token) return;

    const headers = {
      Authorization: `Bearer ${token}`,
      'X-Login-Email': emailId,
      'X-Customer-Id': customerId,
    };

    const currentIds = new Set(jobIds);

    // Close streams for IDs no longer in the list
    for (const [id, close] of streamsRef.current.entries()) {
      if (!currentIds.has(id)) {
        close();
        streamsRef.current.delete(id);
        doneSetRef.current.delete(id);
      }
    }

    // Clean up done entries for IDs that are no longer in the list
    for (const doneId of doneSetRef.current) {
      if (!currentIds.has(doneId)) {
        doneSetRef.current.delete(doneId);
      }
    }

    // Open streams for new IDs
    for (const jobId of jobIds) {
      if (streamsRef.current.has(jobId)) continue;
      if (doneSetRef.current.has(jobId)) continue; // already received terminal — don't reopen

      const url = `${SSE_STREAM_URL}?type=${encodeURIComponent(type)}&jobId=${encodeURIComponent(jobId)}`;

      const close = openSseStream(
        url,
        (payload: SsePayload) => {
          onStatusRef.current?.(jobId, payload);
          onDataRef.current?.(jobId, payload.data);

          // A terminal event WITH contact-level data (e.g. data.id present) is a
          // per-item completion within a batch job — update the row but keep the
          // stream open for the remaining items.
          // Only close on a job-level terminal: data is null (either an explicit
          // job-level signal from the server, or the synthetic COMPLETED that
          // openSseStream emits when the server closes the connection).
          if (isTerminal(payload.status) && !payload.data) {
            doneSetRef.current.add(jobId);
            onJobDoneRef.current?.(jobId, payload);
            streamsRef.current.get(jobId)?.();
            streamsRef.current.delete(jobId);

            // All streams done?
            if (streamsRef.current.size === 0) {
              onAllDoneRef.current?.();
            }
          }
        },
        {
          headers,
          onError: (err) => onErrorRef.current?.(jobId, err),
          maxRetries,
          baseDelay,
        },
      );

      streamsRef.current.set(jobId, close);
    }

    return () => {
      // Cleanup is handled by jobIds changes above; full cleanup on unmount
    };
  }, [type, JSON.stringify(jobIds), enabled, emailId, customerId]);

  // Full cleanup on unmount
  useEffect(() => {
    return () => {
      for (const close of streamsRef.current.values()) {
        close();
      }
      streamsRef.current.clear();
      doneSetRef.current.clear();
    };
  }, []);
}
